/**
 * CategoryPageShell — shared page skeleton for all Seekscape category pages.
 *
 * Renders:
 *  • Sticky top header  (title bar / section switcher — search lives inline
 *    in the page content's category chip row, not in this header)
 *  • Bottom navigation  (Home / Favorites / Library / Menu tabs)
 *  • Scrollable content area in the middle
 *
 * Accent colours:
 *  • Wallpapers  : #a78bfa (purple)  ← default
 *  • Ringtones   : #fb923c (orange)
 *  • Live Walls  : #38bdf8 (sky)
 *  • Icon Packs  : #34d399 (green)
 */

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, LayoutGrid, User, Compass, Bell, BellOff, Check, ArrowLeft } from 'lucide-react';
import { MiniPlayer } from '@/components/MiniPlayer';

import { useRingtonePlayerState } from '@/contexts/RingtonePlayerContext';
import { haptic } from '@/utils/haptic';
import { useNavigate } from 'react-router-dom';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { useBackButtonClose } from '@/hooks/use-back-button-close';
import { prefetchRoute, routeModules } from '@/App';
import { useAppTheme } from '@/utils/theme-context';
import {
  subscribeManyNotificationsItems,
  updateNotificationsItems,
  NotificationsItemsResponse,
} from '@/lib/collections/notifications';
import { Address } from '@/lib/db-client';
import { toast } from 'sonner';

export type Tab = 'home' | 'favorites' | 'library' | 'sales' | 'menu';

// ── Relative time helper (English) ────────────────────────────────────────────
function formatRelativeTime(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  const d = new Date(unixSeconds * 1000);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

function shortenAddress(addr: string): string {
  if (!addr || addr.length < 8) return addr;
  return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
}

// ── Single notification row ───────────────────────────────────────────────────
interface NotifRowProps {
  notif: NotificationsItemsResponse;
  onMarkRead: (id: string) => void;
  onNavigate: (wallpaperId: string | undefined) => void;
  onClose: () => void;
}

const NotifRow: React.FC<NotifRowProps> = ({ notif, onMarkRead, onNavigate, onClose }) => {
  const { tokens } = useAppTheme();
  const displayName = notif.fromUsername
    ? `@${notif.fromUsername}`
    : `@${shortenAddress(notif.fromUser)}`;

  const handleClick = () => {
    if (!notif.read) {
      onMarkRead(notif.id);
    }
    onClose();
    onNavigate(notif.wallpaperId);
  };

  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      whileHover={{ backgroundColor: 'rgba(167,139,250,0.10)' }}
      whileTap={{ scale: 0.985 }}
      onClick={handleClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        background: notif.read
          ? 'transparent'
          : tokens.bgNotifUnread,
        border: 'none',
        borderBottom: tokens.borderSubtle,
        cursor: 'pointer',
        textAlign: 'left',
        position: 'relative',
        transition: 'background 0.15s ease',
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: 48,
        height: 48,
        borderRadius: 10,
        overflow: 'hidden',
        flexShrink: 0,
        background: 'rgba(167,139,250,0.12)',
        border: '1px solid rgba(167,139,250,0.15)',
      }}>
        {notif.wallpaperThumbnailUrl ? (
          <img
            src={notif.wallpaperThumbnailUrl}
            alt={notif.wallpaperTitle ?? ''}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Bell size={18} color="rgba(167,139,250,0.5)" />
          </div>
        )}
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: 13,
          fontWeight: 600,
          color: tokens.textPrimary,
          fontFamily: "'Syne', sans-serif",
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          <span style={{ color: '#a78bfa' }}>{displayName}</span>
          {' '}published new wallpaper
        </p>
        {notif.wallpaperTitle && (
          <p style={{
            margin: '2px 0 0',
            fontSize: 11,
            color: tokens.textMuted,
            fontFamily: "'DM Mono', monospace",
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {notif.wallpaperTitle}
          </p>
        )}
        <p style={{
          margin: '4px 0 0',
          fontSize: 10,
          color: tokens.textVeryMuted,
          fontFamily: "'DM Mono', monospace",
        }}>
          {formatRelativeTime(notif.createdAt)}
        </p>
      </div>

      {/* Unread dot */}
      {!notif.read && (
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: tokens.accent,
          flexShrink: 0,
          boxShadow: `0 0 6px ${tokens.accentMuted}`,
        }} />
      )}
    </motion.button>
  );
};

interface CategoryPageShellProps {
  /** Currently active bottom-nav tab */
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;

  /** Current authenticated user, or null/undefined when logged out */
  user?: { address: string } | null;
  /** Logout callback */
  onLogout?: () => void | Promise<void>;

  /** Accent colour used for the settings button and active tab highlight */
  accentColor?: string;
  /** Hex string of the accent colour with opacity — derived automatically if omitted */
  accentColorAlpha?: string;

  /** Title displayed in the top header bar. Defaults to "Digital Sanctuary". */
  title?: string;

  /** Optional custom content rendered in place of the plain `title` text — e.g. the
   *  Explore screen's arrow-driven section switcher (‹ Wallpapers ›). When provided,
   *  it replaces the title text entirely; the bell/search icons stay in place. */
  titleSlot?: React.ReactNode;

  /** Optional content pinned to the right edge of the title row (e.g. a compact
   *  icon-only action button). Purely additive — when omitted, the title row
   *  renders exactly as before with no layout change. */
  headerTrailingSlot?: React.ReactNode;

  /** Placeholder text shown in the collapsed search pill */
  searchPlaceholder?: string;

  /** Optional slot rendered *inside* the sticky header, below the top bar row
   *  (used by Wallpapers for the category-pill + price-filter row) */
  headerSlot?: React.ReactNode;

  /** Optional extra content rendered *inside* the floating settings panel,
   *  above the disconnect button. Used by Wallpapers for trending/grid toggles. */
  settingsPanelSlot?: React.ReactNode;

  /** Width of the floating settings panel (default: 260) */
  settingsPanelWidth?: number;

  /** Page body content */
  children: React.ReactNode;

  /**
   * When true, the mini-player is rendered but tapping it will NOT open the
   * full-screen player. Set this on pages where full-screen should be suppressed
   * (e.g. Trash). When false (default), tapping opens the full-screen player.
   */
  suppressMiniPlayerFullScreen?: boolean;

  /**
   * When true, the mini-player is not rendered at all.
   */
  hideMiniPlayer?: boolean;

  /**
   * When true, the bottom nav bar is not rendered. Set this when a fullscreen
   * media viewer (wallpaper / live wallpaper) is open above the page.
   */
  hideBottomNav?: boolean;

  /**
   * When false, the bottom nav bar slides off-screen (translate up) with a
   * smooth transition. Used by pages that want to auto-hide the nav on scroll.
   * Defaults to true (nav visible).
   */
  navScrollVisible?: boolean;

  /** Number of unread notifications. When > 0 shows a red badge on the bell. Only shown when user is logged in. */
  unreadNotificationCount?: number;

  /**
   * Whether the header search bar is currently expanded.
   * When undefined, the component manages this state internally.
   */
  searchOpen?: boolean;
  /** Called when the header search open/close state changes */
  onSearchOpenChange?: (open: boolean) => void;
  /** Current search query value (for the header search input) */
  searchQuery?: string;
  /** Called when the header search query changes */
  onSearchChange?: (q: string) => void;
  /**
   * Content to render in the search results overlay when search is open.
   * Displayed as a scrollable overlay panel below the header.
   */
  searchResultsSlot?: React.ReactNode;
  /**
   * Called with a `markNavigating` function that the parent can store and
   * invoke before calling `navigate(...)` from inside a search result click.
   * Calling it clears the sentinel so the back-button cleanup won't fire
   * `history.back()` on unmount and undo the navigation.
   */
  onSearchMarkNavigatingReady?: (markNavigating: () => void) => void;

  /**
   * When true, the hardware back button will NOT close the search overlay.
   * Used when a viewer that was opened from inside search is currently covering
   * the search panel — in that case the first back press should close the viewer,
   * not the search.
   */
  suppressSearchBackClose?: boolean;
}

export function CategoryPageShell({
  activeTab,
  onTabChange,
  user,
  accentColor = '#a78bfa',
  title = 'Digital Sanctuary',
  titleSlot,
  headerTrailingSlot,
  headerSlot,
  children,
  suppressMiniPlayerFullScreen = false,
  hideMiniPlayer = false,
  hideBottomNav = false,
  navScrollVisible = true,
  unreadNotificationCount = 0,
  searchOpen: searchOpenProp,
  onSearchOpenChange,
  searchQuery = '',
  onSearchChange,
  searchResultsSlot,
  onSearchMarkNavigatingReady,
  suppressSearchBackClose = false,
}: CategoryPageShellProps) {
  const { tokens } = useAppTheme();
  const NAV_ACTIVE = tokens.navActiveColor;
  const { fullScreenOpen: ringtoneFullScreenOpen } = useRingtonePlayerState();
  const navHidden = hideBottomNav || ringtoneFullScreenOpen;
  const navigate = useNavigate();


  // ── Header search state ──────────────────────────────────────────────────
  const [searchOpenInternal, setSearchOpenInternal] = useState(false);
  const searchOpen = searchOpenProp !== undefined ? searchOpenProp : searchOpenInternal;
  const setSearchOpen = useCallback((open: boolean) => {
    setSearchOpenInternal(open);
    onSearchOpenChange?.(open);
  }, [onSearchOpenChange]);

  // Escape key closes search
  useEffect(() => {
    if (!searchOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        onSearchChange?.('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen, setSearchOpen, onSearchChange]);

  // Keep a stable ref for suppressSearchBackClose so the callback can read the
  // latest value without re-registering useBackButtonClose on every render.
  const suppressSearchBackCloseRef = useRef(suppressSearchBackClose);
  useEffect(() => { suppressSearchBackCloseRef.current = suppressSearchBackClose; }, [suppressSearchBackClose]);

  // Back button (history popstate) closes search.
  // When suppressSearchBackClose is true (viewer open on top of search), skip
  // the close so the first back press closes only the viewer, not the search.
  //
  // Clears the query as well as collapsing the input: leaving the query set
  // keeps every section filtered (and the "0 results" empty state on screen),
  // so a back press would look like search was still active. Matches Escape
  // and the explicit X control.
  const handleSearchClose = useCallback(() => {
    if (suppressSearchBackCloseRef.current) {
      // A viewer is covering the search panel — ignore this back press.
      // The HomePage popstate handler will close the viewer instead.
      return;
    }
    setSearchOpen(false);
    onSearchChange?.('');
  }, [setSearchOpen, onSearchChange]);
  const { markNavigating: markSearchNavigating } = useBackButtonClose(searchOpen, handleSearchClose, 'header-search');

  // Expose markSearchNavigating to parent so it can call it before navigating
  // away from a search result click (prevents cleanup from firing history.back()).
  useEffect(() => {
    onSearchMarkNavigatingReady?.(markSearchNavigating);
  }, [onSearchMarkNavigatingReady, markSearchNavigating]);

  // Notifications drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Android hardware back button closes the notifications drawer
  useBackButtonClose(drawerOpen, () => setDrawerOpen(false), 'notifications-drawer');

  // Close the notifications drawer when any part of the app initiates a wallet
  // login flow. On Android (MWA / Phantom intent), a fixed-position overlay that
  // remains mounted while the OS "Allow" dialog is visible will intercept touch
  // events, making the system dialog button un-tappable. Closing the drawer
  // synchronously before login() is called ensures it unmounts before the intent.
  useEffect(() => {
    const handleCloseOverlays = () => setDrawerOpen(false);
    window.addEventListener('poof:close-overlays', handleCloseOverlays);
    return () => window.removeEventListener('poof:close-overlays', handleCloseOverlays);
  }, []);

  // Full notifications subscription (only when drawer is open and user is logged in)
  const { data: notifications } = useRealtimeData<NotificationsItemsResponse[]>(
    subscribeManyNotificationsItems,
    !!user?.address,
    user?.address ?? '',
  );

  const sorted = useMemo(() => {
    const list = notifications ?? [];
    return [...list]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 50);
  }, [notifications]);

  const unreadItems = sorted.filter(n => !n.read);
  const hasUnread = unreadItems.length > 0;

  const handleMarkRead = async (notifId: string) => {
    if (!user?.address) return;
    const notif = sorted.find(n => n.id === notifId);
    if (!notif || notif.read) return;

    const ok = await updateNotificationsItems(user.address, notifId, {
      recipient: Address.publicKey(notif.recipient),
      type: notif.type,
      fromUser: Address.publicKey(notif.fromUser),
      wallpaperId: notif.wallpaperId,
      createdAt: notif.createdAt,
      read: true,
    });
    if (!ok) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.address || !hasUnread) return;
    const results = await Promise.all(
      unreadItems.map(n =>
        updateNotificationsItems(user!.address, n.id, {
          recipient: Address.publicKey(n.recipient),
          type: n.type,
          fromUser: Address.publicKey(n.fromUser),
          wallpaperId: n.wallpaperId,
          createdAt: n.createdAt,
          read: true,
        })
      )
    );
    const failed = results.filter(r => !r).length;
    if (failed > 0) {
      toast.error(`Failed to mark ${failed} notification${failed !== 1 ? 's' : ''} as read`);
    }
  };

  const handleNavigateToWallpaper = (wallpaperId: string | undefined) => {
    if (!wallpaperId) return;
    // Navigate to home with ?open= so the viewer auto-opens for the item
    navigate(`/?open=${encodeURIComponent(wallpaperId)}`);
  };

  const baseNavItems: { id: Tab; Icon: React.ElementType; label: string }[] = [
    { id: 'home', Icon: Compass, label: 'Explore' },
    { id: 'library', Icon: LayoutGrid, label: 'Library' },
    { id: 'favorites', Icon: Heart, label: 'Favorites' },
    { id: 'menu', Icon: User, label: 'Profile' },
  ];
  const NAV_ITEMS = baseNavItems;

  // ── Swipe-to-cycle nav ───────────────────────────────────────────────────
  const navSwipeStart = useRef<{ x: number; y: number } | null>(null);
  const navIsDragging = useRef(false);
  // Minimum horizontal distance (px) to count as a swipe
  const SWIPE_THRESHOLD = 40;

  const handleNavSwipeStart = useCallback((x: number, y: number) => {
    navSwipeStart.current = { x, y };
    navIsDragging.current = false;
  }, []);

  const handleNavSwipeEnd = useCallback((endX: number, endY: number) => {
    if (!navSwipeStart.current) return;
    const dx = endX - navSwipeStart.current.x;
    const dy = endY - navSwipeStart.current.y;
    navSwipeStart.current = null;
    navIsDragging.current = false;
    // Ignore if more vertical than horizontal (user scrolling the page)
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    const currentIndex = NAV_ITEMS.findIndex(item => item.id === activeTab);
    if (dx < 0) {
      // Swipe left → next tab
      const next = NAV_ITEMS[(currentIndex + 1) % NAV_ITEMS.length];
      haptic.light();
      if (next.id !== 'home') window.history.pushState({ page: 'sub' }, '');
      onTabChange(next.id);
    } else {
      // Swipe right → previous tab
      const prev = NAV_ITEMS[(currentIndex - 1 + NAV_ITEMS.length) % NAV_ITEMS.length];
      haptic.light();
      if (prev.id !== 'home') window.history.pushState({ page: 'sub' }, '');
      onTabChange(prev.id);
    }
  }, [activeTab, NAV_ITEMS, onTabChange]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: tokens.bg,
        color: tokens.textPrimary,
        fontFamily: "'Syne', sans-serif",
        overflowX: 'hidden',
      }}
    >
      {/* ─── Notifications Drawer (portaled to body, z-index above all UI) ── */}
      {createPortal(
        <AnimatePresence>
          {drawerOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                key="notif-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                onClick={() => setDrawerOpen(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.72)',
                  zIndex: 20000,
                }}
              />
              {/* Panel — slides in from left */}
              <motion.div
                key="notif-panel"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100vw',
                  zIndex: 20001,
                  willChange: 'transform',
                  background: tokens.bgNotifDrawer,
                  color: tokens.textPrimary,
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Drawer header */}
                <div style={{
                  flexShrink: 0,
                  background: tokens.bgNotifDrawer,
                  paddingTop: `calc(14px + env(safe-area-inset-top, 0px))`,
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '0 12px 8px',
                  }}>
                    {/* Back */}
                    <button
                      onClick={() => setDrawerOpen(false)}
                      style={{
                        width: 36, height: 36,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: tokens.textMuted,
                        padding: 0, flexShrink: 0,
                      }}
                      aria-label="Back"
                    >
                      <ArrowLeft size={22} strokeWidth={2} />
                    </button>

                    {/* Title */}
                    <span style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 15,
                      fontWeight: 800,
                      color: '#a78bfa',
                      fontFamily: "'Syne', sans-serif",
                      letterSpacing: '-0.02em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      Notifications
                    </span>

                    {/* Mark all read */}
                    <motion.button
                      whileTap={{ scale: 0.92 }}
                      onClick={handleMarkAllRead}
                      disabled={!hasUnread}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 9px',
                        borderRadius: 9999,
                        background: hasUnread ? 'rgba(167,139,250,0.12)' : 'transparent',
                        border: `1px solid ${hasUnread ? 'rgba(167,139,250,0.3)' : 'transparent'}`,
                        color: hasUnread ? '#a78bfa' : 'rgba(240,240,245,0.25)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        fontFamily: "'Syne', sans-serif",
                        cursor: hasUnread ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Check size={11} strokeWidth={2.5} />
                      Mark all
                    </motion.button>
                  </div>
                </div>

                {/* Drawer content */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {!user ? (
                    // Not logged in
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', padding: '80px 16px', gap: 16, textAlign: 'center',
                    }}>
                      <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(167,139,250,0.1)',
                        border: '1px solid rgba(167,139,250,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <BellOff size={24} color="#a78bfa" strokeWidth={1.5} />
                      </div>
                      <p style={{
                        fontSize: 14, fontWeight: 700, color: '#f0f0f5',
                        margin: 0, fontFamily: "'Syne', sans-serif",
                      }}>
                        Sign in to see notifications
                      </p>
                    </div>
                  ) : sorted.length === 0 ? (
                    // Empty state
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', padding: '80px 16px', gap: 16, textAlign: 'center',
                    }}>
                      <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(167,139,250,0.08)',
                        border: '1px solid rgba(167,139,250,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <BellOff size={24} color="rgba(167,139,250,0.5)" strokeWidth={1.5} />
                      </div>
                      <p style={{
                        fontSize: 14, fontWeight: 700, color: 'rgba(240,240,245,0.5)',
                        margin: 0, fontFamily: "'Syne', sans-serif",
                      }}>
                        No notifications
                      </p>
                      <p style={{
                        fontSize: 12, color: 'rgba(240,240,245,0.3)',
                        margin: 0, lineHeight: 1.55,
                        fontFamily: "'Syne', sans-serif",
                      }}>
                        Subscribe to authors to get notified
                      </p>
                    </div>
                  ) : (
                    // Notification list
                    <div style={{ paddingBottom: 40 }}>
                      <AnimatePresence>
                        {sorted.map(notif => (
                          <NotifRow
                            key={notif.id}
                            notif={notif}
                            onMarkRead={handleMarkRead}
                            onNavigate={handleNavigateToWallpaper}
                            onClose={() => setDrawerOpen(false)}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Override webkit autofill / mobile browser default input backgrounds */}
      <style>{`
        .seekscape-search-input {
          -webkit-appearance: none !important;
          appearance: none !important;
          background-color: transparent !important;
          background-image: none !important;
          color-scheme: ${tokens.bg === '#f5f4f0' ? 'light' : 'dark'};
          box-shadow: none !important;
        }
        .seekscape-search-input:hover,
        .seekscape-search-input:focus,
        .seekscape-search-input:active {
          background-color: transparent !important;
          background-image: none !important;
          box-shadow: none !important;
        }
        .seekscape-search-input:-webkit-autofill,
        .seekscape-search-input:-webkit-autofill:hover,
        .seekscape-search-input:-webkit-autofill:focus,
        .seekscape-search-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px ${tokens.bgHeader} inset !important;
          box-shadow: 0 0 0 1000px ${tokens.bgHeader} inset !important;
          -webkit-text-fill-color: ${tokens.textPrimary} !important;
          caret-color: ${tokens.textPrimary} !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      {/* ─── Sticky top bar row (Bell | Title | Search) ─────────────
           zIndex 210 — the full-screen player (220) stacks above this,
           covering the topbar completely when the player is open.
           ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 210,
          background: tokens.bgHeader,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {/* ── Top bar: section title / switcher ──
             Bell and search were removed from here — notifications are no
             longer reachable from the header, and search now lives inline in
             the category chip row rendered by the page content (below). The
             title/switcher container spans the full row width and centers
             itself, so removing those side icons leaves the row correctly
             balanced with no leftover gaps. ── */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 16px',
          paddingTop: `calc(14px + env(safe-area-inset-top, 0px))`,
          minHeight: 66,
          boxSizing: 'border-box',
        }}>
          {/* Title — left-aligned when plain text. When `titleSlot` is provided
               (e.g. Explore's arrow section switcher) it replaces the plain
               text entirely and is centered in the row. */}
          <div style={{
            flex: 1,
            minWidth: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: titleSlot ? 'row' : 'column',
            alignItems: titleSlot ? 'center' : 'flex-start',
            justifyContent: 'center',
          }}>
            {titleSlot ? titleSlot : (
              <span style={{
                fontSize: 20,
                fontWeight: 800,
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '-0.02em',
                textAlign: 'left',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 'fit-content',
                maxWidth: '100%',
                ...(title === 'SeekScape' || title === 'Preview' || title === 'Favorites' || title === 'Library' || title === 'Sales' || title === 'Profile'
                  ? {
                      backgroundImage:
                        title === 'Favorites'
                          ? 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)'
                          : title === 'Library'
                          ? 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)'
                          : title === 'Sales'
                          ? 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)'
                          : title === 'Profile'
                          ? 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)'
                          : 'linear-gradient(90deg, #3b1fb5 0%, #7c3aed 50%, #b96bff 100%)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }
                  : { color: tokens.accent }),
              }}>
                {title}
              </span>
            )}
          </div>

          {/* Trailing action — pinned to the right edge, only rendered when the
               page supplies one (e.g. Library's bulk-trash icon). Absent by
               default, so no other page's header layout changes. */}
          {headerTrailingSlot && (
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', marginLeft: 8 }}>
              {headerTrailingSlot}
            </div>
          )}
        </div>

        {/* ── Search results overlay — portaled to body, z-index above page but below modals ── */}
        {createPortal(
          <AnimatePresence>
            {searchOpen && searchQuery && searchResultsSlot && (
              <>
                {/* Backdrop (does not close search — tap X or back to close) */}
                <motion.div
                  key="search-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: 'fixed',
                    // 63px header + ~58px for the search/category chip row
                    // that now sits directly below it (search lives there,
                    // not in the header), so the overlay never covers it.
                    top: `calc(121px + env(safe-area-inset-top, 0px))`,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 5000,
                    // Allow tap-through to close via X button only
                    pointerEvents: 'none',
                  }}
                />
                {/* Results panel */}
                <motion.div
                  key="search-results-panel"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                  style={{
                    position: 'fixed',
                    // 63px header + ~58px for the search/category chip row
                    // that now sits directly below it (search lives there,
                    // not in the header), so the overlay never covers it.
                    top: `calc(121px + env(safe-area-inset-top, 0px))`,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 5001,
                    background: tokens.bgSearchPanel,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 120 }}>
                    {searchResultsSlot}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>

      {/* ─── Sticky secondary header row (filter bar) ──
           zIndex 100 — stays BELOW the full-screen player (220) so that
           category pills and filter rows are hidden behind the player
           when it is open.
           top is set to the height of the top bar row so this element
           sticks directly below the top bar during normal scrolling.
           ─────────────────────────────────────────────────────────── */}
      {headerSlot && (
        <div
          style={{
            position: 'sticky',
            top: `calc(63px + env(safe-area-inset-top, 0px))`,
            zIndex: 100,
            background: tokens.bg,
          }}
        >
          {headerSlot}
        </div>
      )}

      {/* ─── Page content ──────────────────────────────────────────── */}
      {/* paddingBottom clears the floating bottom nav (≈60px pill + 16px offset + 24px gap) */}
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>{children}</div>

      {/* ─── Mini player (floating above the bottom nav) ─────────────
           When the nav is hidden (modal open) or slid away by scroll, drop the
           player to the bottom edge (16px) so it fills the space the nav left
           behind instead of hovering with a gap underneath. */}
      {!hideMiniPlayer && (
        <MiniPlayer
          disableFullScreen={suppressMiniPlayerFullScreen}
          bottomOffsetPx={
            navHidden || !navScrollVisible ? 16 : 80
          }
        />
      )}

      {/* ─── Gradient fade overlay above the bottom nav ───────────── */}
      {!navHidden && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 120,
            pointerEvents: 'none',
            zIndex: 10000,
            background: tokens.navGradient,
            transform: navScrollVisible ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 300ms ease',
            willChange: 'transform',
          }}
        />
      )}

      {/* ─── Bottom navigation — Tab Bar ─────────────────────── */}
      {!navHidden && (
      <nav
        onTouchStart={e => handleNavSwipeStart(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={e => handleNavSwipeEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY)}
        onPointerDown={e => { if (e.pointerType !== 'touch') handleNavSwipeStart(e.clientX, e.clientY); }}
        onPointerUp={e => { if (e.pointerType !== 'touch') handleNavSwipeEnd(e.clientX, e.clientY); }}
        style={{
          position: 'fixed',
          bottom: `calc(16px + env(safe-area-inset-bottom, 0px))`,
          left: '50%',
          transform: `translateX(-50%) translateY(${navScrollVisible ? '0' : 'calc(100% + 32px + env(safe-area-inset-bottom, 0px))'})`,
          transition: 'transform 300ms ease',
          willChange: 'transform',
          width: 'min(90vw, 28rem)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '8px 12px',
          background: tokens.bgNav,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 9999,
          border: `1px solid ${tokens.border}`,
          boxShadow: '0 0 20px rgba(187,134,252,0.1), 0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        {NAV_ITEMS.map(({ id, Icon, label }) => {
          const active = activeTab === id;
          return (
            <motion.button
              key={id}
              whileTap={{ scale: 0.95 }}
              onMouseEnter={() => {
                // Hovering "Profile" tab often precedes navigating to a user profile
                // page — warm the UserProfilePage chunk now so the transition is instant.
                if (id === 'menu') prefetchRoute(routeModules.userProfile);
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                haptic.light();
                if (id !== 'home') {
                  window.history.pushState({ page: 'sub' }, '');
                }
                onTabChange(id);
              }}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                padding: '7px 12px',
                borderRadius: 9999,
                border: 'none',
                background: active ? NAV_ACTIVE : 'transparent',
                color: active ? tokens.navActiveFg : tokens.navInactiveFg,
                boxShadow: active ? '0 0 12px rgba(70,245,224,0.2)' : 'none',
                cursor: 'pointer',
                transition: 'all 200ms ease',
              }}
            >
              <Icon
                size={16}
                strokeWidth={active ? 2.2 : 1.8}
                color={active ? tokens.navActiveFg : tokens.navInactiveFg}
              />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: active ? 700 : 500,
                  color: active ? tokens.navActiveFg : tokens.navInactiveFg,
                  fontFamily: "'Syne', sans-serif",
                  letterSpacing: '0.02em',
                  lineHeight: 1,
                }}
              >
                {label}
              </span>
            </motion.button>
          );
        })}
      </nav>
      )}


    </div>
  );
}

export default CategoryPageShell;
